import { Button } from '@design-system';
import { QuestionnaireProgress } from '@/features/advisor-onboarding/questionnaire/components/questionnaire-progress';

import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

import { useQuestionScreen } from '@/features/advisor-onboarding/questionnaire/contexts/question-screen-context';

import { type ReactNode } from 'react';

interface QuestionScreenLayoutProps {
  children: ReactNode;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  showNext?: boolean;
  showSkip?: boolean;
  skipLabel?: string;
  onSkip?: () => void;
  showBack?: boolean;
}

export function QuestionScreenLayout({
  children,
  onNext,
  nextLabel = 'Next',
  nextDisabled = false,
  showNext = true,
  showSkip = false,
  skipLabel = 'Skip',
  onSkip,
  showBack = true,
}: QuestionScreenLayoutProps) {
  const { resolved, progress, isSubmitting, onBack, submitSkip } = useQuestionScreen();

  const handleSkip = onSkip ?? submitSkip;

  return (
    <div className="flex flex-col items-center px-4 sm:px-0">
      <div className="w-full max-w-xl space-y-8">
        <QuestionnaireProgress
          current={progress.current}
          estimatedTotal={progress.estimatedTotal}
        />
        <div className="space-y-2">
          <h2 className="text-xl font-semibold whitespace-pre-line text-gray-950 sm:text-2xl">
            {resolved.prompt}
          </h2>
          {resolved.helperText && (
            <p className="text-foreground text-sm sm:text-base">{resolved.helperText}</p>
          )}
        </div>

        <div className="space-y-4">{children}</div>

        <div className="flex items-center justify-between gap-3 pt-2">
          {showBack ? (
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
            >
              <ChevronLeft />
              Back
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3">
            {showSkip && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleSkip}
              >
                {skipLabel}
              </Button>
            )}
            {showNext && (
              <Button
                type="button"
                onClick={onNext}
                disabled={nextDisabled || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    {nextLabel}
                    <ChevronRight />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
